import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Sparkles } from "lucide-react";
import { fetchPackage } from "@/lib/api";
import InquiryForm from "@/components/InquiryForm";
import { Button } from "@/components/ui/button";

export default function PackageDetail() {
  const { id } = useParams();
  const [pkg, setPkg] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchPackage(id).then(setPkg).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 pt-32 text-center">
        <div>
          <h1 className="font-serif text-4xl">Package not found</h1>
          <Link to="/packages" className="overline mt-6 inline-block text-primary">← Back to all packages</Link>
        </div>
      </div>
    );
  }
  if (!pkg) return <div className="pt-40 text-center overline text-muted-foreground">Loading</div>;

  return (
    <div data-testid={`package-detail-page-${pkg.id}`} className="pt-24">
      <section className="relative h-[70vh] overflow-hidden">
        <img src={pkg.hero_image} alt={pkg.name} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black" />
        <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-end px-6 pb-16 md:px-12">
          <Link to="/packages" className="overline mb-8 inline-flex items-center gap-2 text-primary">
            <ArrowLeft size={14} strokeWidth={1.5} /> All Packages
          </Link>
          <span className="overline text-primary">{pkg.tagline}</span>
          <h1 className="mt-4 font-serif text-5xl leading-none tracking-tight md:text-8xl">{pkg.name}</h1>
          <div className="mt-6 flex flex-wrap items-baseline gap-8">
            <span className="overline text-foreground/70">{pkg.duration}</span>
            <span className="font-serif text-3xl text-primary md:text-4xl">
              from ₹{pkg.price_from.toLocaleString("en-IN")}
            </span>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-6 py-24 md:px-12 md:py-32">
        <div className="grid gap-16 md:grid-cols-12">
          <div className="md:col-span-7">
            <span className="overline text-primary">Overview</span>
            <p className="mt-6 font-serif text-3xl leading-snug md:text-4xl">{pkg.description}</p>

            <div className="mt-16">
              <span className="overline text-primary">Signature Inclusions</span>
              <ul className="mt-8 space-y-5">
                {pkg.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-4 border-b hairline pb-5 font-sans-lux text-lg text-foreground/85">
                    <Sparkles className="mt-1 shrink-0 text-primary" size={20} strokeWidth={1.25} />
                    {h}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="md:col-span-5">
            <div className="sticky top-32 border hairline bg-secondary/30 p-8 md:p-10">
              <span className="overline text-primary">Reserve</span>
              <h3 className="mt-4 font-serif text-3xl">Speak with concierge</h3>
              <p className="mt-4 font-sans-lux text-sm leading-relaxed text-foreground/70">
                We open a limited number of {pkg.name} journeys per Snan window. Availability is confirmed within 24 hours.
              </p>
              <ul className="mt-8 space-y-4 font-sans-lux text-sm text-foreground/80">
                <li className="flex items-center gap-3"><Check size={14} strokeWidth={1.5} className="text-primary" /> Fully private itinerary</li>
                <li className="flex items-center gap-3"><Check size={14} strokeWidth={1.5} className="text-primary" /> Guaranteed Bhasma Aarti seating</li>
                <li className="flex items-center gap-3"><Check size={14} strokeWidth={1.5} className="text-primary" /> Best for {pkg.best_for}</li>
              </ul>
              <Link to={`/plan-your-visit?package=${pkg.id}`}>
                <Button
                  data-testid={`detail-inquire-${pkg.id}`}
                  className="mt-10 w-full rounded-none bg-primary py-7 text-primary-foreground hover:bg-primary/90"
                >
                  <span className="overline">Inquire Now</span>
                </Button>
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <section className="border-t hairline bg-black py-24 md:py-32">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <div className="grid gap-16 md:grid-cols-12">
            <div className="md:col-span-5">
              <span className="overline text-primary">Begin</span>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl">Reserve <em className="italic">{pkg.name}</em>.</h2>
              <p className="mt-6 font-sans-lux text-foreground/70">A senior concierge will respond personally within 24 hours.</p>
            </div>
            <div className="md:col-span-7">
              <InquiryForm presetPackageId={pkg.id} />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
